/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Onset ASSIGNMENT — laying a burst's segments onto acoustic evidence.
 *
 * The DETECTOR half of the old `services/onsets.ts` (spectral flux, peak
 * picking, and the span/label/envelope shapes) stayed live in
 * `services/onsetDetect.ts`, because CorrectPage's and CompletedPage's waveform
 * drag magnets are built from it. What is left here is stage 5 of the legacy
 * repair tower and its dip detector, reached only from the legacy pipeline.
 *
 * WHY THIS EXISTS
 * ---------------
 * `refineBoundaries` fixes the time AXIS (global warp + banded DTW + a snap onto
 * silence edges). Measured on the user's 44.8 s Jordanian-Arabic recording it
 * does that well: mean boundary error against the audio's own silences drops to
 * ~20 ms. What it CANNOT fix is where the boundaries sit INSIDE a burst of
 * continuous speech, because there is no silence in there to snap to.
 *
 * And that is where the first pass fails. Forensics on the best-scoring draw of
 * that file:
 *
 *   32 of 52 segments sat in zero-gap contiguous runs — every internal boundary
 *   a proportional guess with no acoustic evidence under it;
 *   nine durations were physically impossible ("انا" 60 ms, five words sharing
 *   0.32 s), while their neighbours swallowed whole bursts ("انه" 2.65 s);
 *   3.16 s of detected speech had no label over it at all.
 *
 * The alignment score cannot see any of this as *placement* error — it measures
 * physical plausibility, and re-rolling the draw does not help, because the model
 * invents proportional splits every time.
 *
 * WHAT THIS MODULE DOES
 * ---------------------
 * Uses the evidence `../onsetDetect` supplies, and adds the weaker kind.
 *
 * 1. ONSETS — supplied by `services/onsetDetect.detectOnsets`. Peaks in
 *    spectral flux: where energy appears in a new part of the spectrum, i.e.
 *    where a syllable starts.
 *
 * 2. DIPS. Local energy minima inside a burst. Weaker evidence than an onset —
 *    a dip can be a stop consonant rather than a word break — but it is what is
 *    left when two words run together with no spectral discontinuity.
 *
 * 3. ASSIGNMENT. A dynamic program lays the burst's K segments onto those
 *    candidates in order, minimising
 *        w_move · |candidate − the DTW start|
 *      + w_dur  · how implausible the resulting duration is for the label
 *      + w_onset· (1 − onset strength)
 *    The candidate list always contains each segment's current start, so the DP
 *    can always reproduce the status quo — it never fails for lack of options,
 *    it only declines to move.
 *
 * 4. GUARD. Every burst is scored before and after with the same span/recall/
 *    plausibility mix `alignmentScore` uses, and the whole stage is scored the
 *    same way; a burst that gets materially worse keeps its old boundaries, and
 *    a stage that gets materially worse is discarded outright.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ----------------------------------
 * Move a segment from one burst to another. It was tried: a dynamic program over
 * (burst, run of segments) that let a run migrate to a neighbouring burst, meant
 * to rescue the five words the first pass crammed into a 0.32 s burst at 26.70 s
 * when they were plainly spoken in the 2.65 s burst at 28.46 s. On the
 * calibration file it fixed that case and broke four others, because nothing in
 * the cost function knows WHICH words are in which burst — it only knows what is
 * plausible, so it cheerfully dragged "بدي" 1.4 s backwards into the previous
 * burst to fill it. Plausible and wrong is the failure mode this whole module
 * exists to remove, so segments stay in the burst they overlap. A burst that
 * genuinely holds the wrong labels is a first-pass RECALL failure, and the stage
 * that can fix it is burst re-segmentation, which is allowed to ask the model
 * what is actually in there.
 *
 * DEPENDENCIES
 * ------------
 * `src/lib/num` (pure, zero project imports) and `services/onsetDetect` (pure
 * too). Everything below is a pure function over Float32Arrays and plain
 * objects, so the module can be exercised in a Node harness with no browser and
 * no audio library. That is not incidental: the DP weights were calibrated by
 * replaying the stored run log offline.
 */

import { round3 } from '../../lib/num';
import { ONSET_DEFAULTS, type EnvelopeLike, type LabelledSpan, type OnsetCandidate, type SpanSec } from '../onsetDetect';

/* ------------------------------------------------------------------ */
/* 2 — energy dips                                                     */
/* ------------------------------------------------------------------ */

/** How much a valley must fall below its flanking peaks to count as a dip. */
const DIP_PROMINENCE = 0.72;

/**
 * Local energy minima inside `[startSec, endSec)`.
 *
 * Weaker evidence than a spectral onset — the silence inside a /k/ looks exactly
 * like the gap between two words — so dips are only ever used to make up the
 * numbers when a burst has fewer flux peaks than it has segments. Their strength
 * is deliberately reported low.
 */
export function detectDips(
  env: EnvelopeLike,
  startSec: number,
  endSec: number,
  options: { minSeparationSec?: number } = {},
): OnsetCandidate[] {
  const hop = env.hopSec;
  const from = Math.max(1, Math.floor(startSec / hop));
  const to = Math.min(env.rms.length - 1, Math.ceil(endSec / hop));
  if (to - from < 3) return [];

  const minSep = Math.max(1, Math.round((options.minSeparationSec ?? ONSET_DEFAULTS.minSeparationSec) / hop));
  const out: OnsetCandidate[] = [];
  let lastFrame = -Infinity;

  for (let i = from + 1; i < to - 1; i++) {
    const v = env.rms[i];
    if (v > env.rms[i - 1] || v > env.rms[i + 1]) continue;

    // Flanking maxima, so a plateau does not register as a valley.
    let left = v;
    for (let j = i; j >= from && i - j <= minSep * 2; j--) if (env.rms[j] > left) left = env.rms[j];
    let right = v;
    for (let j = i; j < to && j - i <= minSep * 2; j++) if (env.rms[j] > right) right = env.rms[j];
    const flank = Math.min(left, right);
    if (flank <= 0 || v > flank * DIP_PROMINENCE) continue;

    if (i - lastFrame < minSep) continue;
    lastFrame = i;
    out.push({
      timeSec: i * hop,
      // A deep valley is better evidence than a shallow one, but never as good
      // as a spectral onset — hence the 0.5 ceiling.
      strength: Math.min(0.5, 0.5 * (1 - v / flank)),
      source: 'dip',
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* 3 — how long should this label take to say?                         */
/* ------------------------------------------------------------------ */

/** Arabic harakat, tanwin, superscript alef, Quranic marks and tatweel. */
const ARABIC_MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;

/** Letters and digits in a label, diacritics and punctuation excluded. */
export function letterCount(text: string): number {
  return ((text ?? '').replace(ARABIC_MARKS, '').match(/[\p{L}\p{N}]/gu) ?? []).length;
}

/**
 * Rough syllable count.
 *
 * Arabic is written without short vowels, so the letters ARE roughly the
 * consonant/long-vowel skeleton and a CV syllable is about two of them:
 * مرحبا (5 letters) -> 3 (mar-ha-ba), امجد (4) -> 2 (am-jad), يعجبكم (6) -> 3.
 * Latin script gets vowel-group counting, which is the usual approximation.
 *
 * This only has to be right to within a syllable — it feeds a soft duration
 * penalty, not a hard constraint.
 */
export function estimateSyllables(text: string): number {
  const clean = (text ?? '').replace(ARABIC_MARKS, '');
  const arabic = (clean.match(/[ء-يٱ-ۓ]/gu) ?? []).length;
  const latin = (clean.match(/[a-z]/giu) ?? []).length;

  if (arabic >= latin) {
    // Multi-word labels get a syllable per word boundary as well.
    const words = clean.split(/\s+/).filter(Boolean).length || 1;
    return Math.max(words, Math.round(arabic / 2));
  }

  const groups = clean.toLowerCase().match(/[aeiouy]+/g) ?? [];
  let n = groups.length;
  if (/e\b/.test(clean.toLowerCase()) && n > 1) n--; // silent final e
  return Math.max(1, n || Math.round(latin / 3));
}

/**
 * Plausible duration window for a label, seconds.
 *
 * Speech runs 120–200 ms per syllable in conversation; the bounds here are
 * deliberately wider than that (75 ms at the fast end, 380 ms at the slow end,
 * plus a fixed margin for the onset and release) because the penalty they feed
 * is meant to reject the *physically impossible*, not to enforce a cadence.
 *
 * Fillers and noise get no upper bound worth the name: an elongated "ummmm" or
 * a door closing really can run for two seconds, and cutting one short mid-hum
 * is the one failure the existing pipeline explicitly forbids.
 */
export function expectedDurationRange(
  text: string,
  kind: string,
): { minSec: number; maxSec: number } {
  const syllables = estimateSyllables(text);
  if (kind === 'filler' || kind === 'noise' || kind === 'unclear') {
    // The floor is not the pipeline's 60 ms: a hesitation clipped to a tenth of
    // a second is not audible as a hesitation, and the DP will happily produce
    // one if you let it buy room for the word next door. (Measured: "ايه" came
    // back at 105 ms with the floor at 60 ms.)
    return { minSec: 0.12, maxSec: 3.0 };
  }
  return {
    minSec: 0.055 + syllables * 0.075,
    maxSec: 0.18 + syllables * 0.38,
  };
}

/**
 * Hard floor on a segment's length.
 *
 * 60 ms is the pipeline-wide minimum; a `word` whose label is short (≤ 6
 * letters, i.e. one or two syllables) gets 100 ms instead, because below that it
 * is not a word, it is the proportional-split artefact this module exists to
 * remove. The exception — a short word alone on a burst too small to hold it —
 * is handled by the caller, which never re-cuts a single-segment burst.
 */
export function minimumDurationFor(seg: LabelledSpan): number {
  // 0.12 rather than the 0.10 the invariant strictly demands, because 0.12 is
  // `IMPLAUSIBLE_SHORT_SEC` in burstResegment: anything below it is what the
  // rest of the pipeline already calls pathological, and there is no sense in a
  // stage that emits boundaries the next stage immediately flags. Measured: at a
  // 0.10 floor the DP produced two 105 ms words on the calibration file, both of
  // which the alignment score then counted as impossible.
  if (seg.kind === 'word' && letterCount(seg.heard) <= 6) return 0.12;
  if (seg.kind !== 'word') return 0.12;
  return 0.06;
}

/** 0 inside the plausible window, growing with relative error outside it. */
function durationPenalty(seg: LabelledSpan, durSec: number): number {
  const { minSec, maxSec } = expectedDurationRange(seg.heard, seg.kind);
  if (durSec < minSec) return (minSec - durSec) / minSec;
  if (durSec > maxSec) return (durSec - maxSec) / maxSec;
  return 0;
}

/* ------------------------------------------------------------------ */
/* 4 — the per-burst dynamic program                                   */
/* ------------------------------------------------------------------ */

export interface AlignWeights {
  /** Per second of movement away from the DTW boundary. */
  move: number;
  /** Per unit of relative duration implausibility. */
  duration: number;
  /** Per unit of missing onset strength. */
  onset: number;
}

export const DEFAULT_WEIGHTS: AlignWeights = {
  move: 0.9,
  duration: 1.6,
  onset: 0.55,
};

/** Charged per segment when a burst is too small to hold its group properly. */
const CRAM_PENALTY = 6;

export interface BurstPlacement {
  /** Chosen start for each segment in the group, ascending. */
  starts: number[];
  /** Chosen end for each segment in the group. */
  ends: number[];
  cost: number;
  onsetCount: number;
  candidateCount: number;
  crammed: boolean;
}

/**
 * Lay `segs` onto `candidates` inside `[burstStart, burstEnd]`, in order.
 *
 * The DP state is "segment i starts at candidate j"; the transition charges the
 * duration penalty for the segment that just closed. Order and non-overlap are
 * structural — j is required to increase — so no repair pass is needed
 * afterwards.
 *
 * Returns null only when the burst physically cannot hold the group even at the
 * relaxed minimum, which the caller reads as "leave this burst alone".
 */
export function placeSegmentsInBurst(
  segs: readonly LabelledSpan[],
  burstStart: number,
  burstEnd: number,
  candidates: readonly OnsetCandidate[],
  weights: AlignWeights = DEFAULT_WEIGHTS,
): BurstPlacement | null {
  const K = segs.length;
  if (K === 0) return null;
  const burstDur = burstEnd - burstStart;
  if (burstDur <= 0) return null;

  /*
   * A burst too small to hold its segments at their proper minimum is not a
   * placement problem, it is a LISTING problem: the first pass put labels here
   * that were spoken somewhere else (measured: five words assigned to 0.32 s of
   * audio). There is no arrangement of five words in a third of a second that is
   * not a lie, so the DP is skipped entirely and the region is divided evenly —
   * which at least spreads them across the audio they were given — and flagged,
   * so the caller's guard and the run log both see it for what it is.
   */
  const mins = segs.map((s) => minimumDurationFor(s));
  const required = mins.reduce((a, b) => a + b, 0);
  if (required > burstDur) {
    const share = burstDur / K;
    const starts = segs.map((_, k) => burstStart + k * share);
    return {
      starts,
      ends: starts.map((_, k) => (k + 1 < K ? starts[k + 1] : burstEnd)),
      cost: CRAM_PENALTY * K,
      onsetCount: candidates.filter((c) => c.source === 'flux').length,
      candidateCount: 0,
      crammed: true,
    };
  }

  // Candidate times, clamped into the burst, deduplicated, always including the
  // burst start so the first segment has somewhere sensible to land.
  // A candidate is admissible if the LAST segment could still fit after it; the
  // per-segment "and everything after it must fit too" bound is `latest[]`,
  // applied in the DP. Using `required` here instead was a real bug: it threw
  // away every candidate in the final stretch of the burst, and a four-word
  // burst whose only workable layout ended at 4.29 s came back unplaceable.
  const lastMin = mins[K - 1];
  const pool = new Map<number, OnsetCandidate>();
  const put = (c: OnsetCandidate) => {
    const t = round3(c.timeSec);
    if (t < burstStart - 1e-9 || t > burstEnd - lastMin + 1e-9) return;
    const prev = pool.get(t);
    if (!prev || c.strength > prev.strength) pool.set(t, { ...c, timeSec: t });
  };
  put({ timeSec: burstStart, strength: 0, source: 'anchor' });
  for (const c of candidates) put(c);
  // The status quo is always reachable: every segment's own DTW start is a
  // candidate, so the DP can decline to move and the cost comparison is honest.
  for (const s of segs) put({ timeSec: s.startSec, strength: 0, source: 'anchor' });

  const cand = [...pool.values()].sort((a, b) => a.timeSec - b.timeSec);
  const C = cand.length;
  if (C < 1) return null;

  const INF = Number.POSITIVE_INFINITY;
  const startCost = (k: number, j: number): number =>
    weights.move * Math.abs(cand[j].timeSec - segs[k].startSec) +
    weights.onset * (1 - cand[j].strength);

  // Latest candidate segment k may start at, leaving room for the rest.
  const latest = new Float64Array(K);
  {
    let tail = 0;
    for (let k = K - 1; k >= 0; k--) {
      tail += mins[k];
      latest[k] = burstEnd - tail;
    }
  }

  const dp: Float64Array[] = [];
  const back: Int32Array[] = [];
  for (let k = 0; k < K; k++) {
    dp.push(new Float64Array(C).fill(INF));
    back.push(new Int32Array(C).fill(-1));
  }

  /*
   * The first segment starts where the window starts. Not a simplification — a
   * correctness fix. Left free, the DP reliably starts late: a label whose
   * plausible ceiling is 0.94 s sitting over 2.71 s of speech is cheaper to
   * shorten (saving 1.6 x the relative duration error) than to anchor (costing
   * 0.9 per second of movement), so it walks forward and leaves the front of the
   * burst unlabelled. Measured: 1.07 s of speech abandoned at 22.94 s, and the
   * file's speech recall down from 0.989 to 0.909. Something IS being said at
   * the start of the burst and the first label is the only one available for it.
   */
  dp[0][0] = startCost(0, 0);

  for (let k = 1; k < K; k++) {
    for (let j = 0; j < C; j++) {
      if (cand[j].timeSec > latest[k] + 1e-9) break;
      let best = INF;
      let bestPrev = -1;
      for (let i = 0; i < j; i++) {
        if (dp[k - 1][i] === INF) continue;
        const dur = cand[j].timeSec - cand[i].timeSec;
        if (dur < mins[k - 1] - 1e-9) continue;
        const total = dp[k - 1][i] + weights.duration * durationPenalty(segs[k - 1], dur);
        if (total < best) {
          best = total;
          bestPrev = i;
        }
      }
      if (bestPrev < 0) continue;
      dp[k][j] = best + startCost(k, j);
      back[k][j] = bestPrev;
    }
  }

  let bestJ = -1;
  let bestCost = INF;
  for (let j = 0; j < C; j++) {
    if (dp[K - 1][j] === INF) continue;
    const dur = burstEnd - cand[j].timeSec;
    if (dur < mins[K - 1] - 1e-9) continue;
    const total = dp[K - 1][j] + weights.duration * durationPenalty(segs[K - 1], dur);
    if (total < bestCost) {
      bestCost = total;
      bestJ = j;
    }
  }
  if (bestJ < 0) return null;

  const starts = new Array<number>(K);
  let j = bestJ;
  for (let k = K - 1; k >= 0; k--) {
    starts[k] = cand[j].timeSec;
    j = back[k][j];
  }
  const ends = starts.map((_, k) => (k + 1 < K ? starts[k + 1] : burstEnd));

  return {
    starts,
    ends,
    cost: bestCost,
    onsetCount: cand.filter((c) => c.source === 'flux').length,
    candidateCount: C,
    crammed: false,
  };
}

/* ------------------------------------------------------------------ */
/* 5 — local scoring, for the per-burst guard                          */
/* ------------------------------------------------------------------ */

/** Same mix as `alignmentScore`, restricted to one burst. */
export function scoreBurst(
  segs: readonly LabelledSpan[],
  env: EnvelopeLike,
  burstStart: number,
  burstEnd: number,
): number {
  const hop = env.hopSec;
  const from = Math.max(0, Math.floor(burstStart / hop));
  const to = Math.min(env.mask.length, Math.ceil(burstEnd / hop));
  if (to <= from) return 0;

  const covered = new Uint8Array(to - from);
  let span = 0;
  let hit = 0;
  let implausible = 0;

  for (const s of segs) {
    const a = Math.max(from, Math.floor(s.startSec / hop));
    const b = Math.min(to, Math.ceil(s.endSec / hop));
    for (let i = a; i < b; i++) {
      covered[i - from] = 1;
      span++;
      if (env.mask[i]) hit++;
    }
    // Count the whole segment's span, including any part outside the burst, so
    // a segment that escapes into silence is scored for it.
    const outside =
      Math.max(0, Math.floor(burstStart / hop) - Math.floor(s.startSec / hop)) +
      Math.max(0, Math.ceil(s.endSec / hop) - Math.ceil(burstEnd / hop));
    span += outside;
    if (durationPenalty(s, s.endSec - s.startSec) > 0.5) implausible++;
  }

  let speech = 0;
  let speechCovered = 0;
  for (let i = from; i < to; i++) {
    if (!env.mask[i]) continue;
    speech++;
    if (covered[i - from]) speechCovered++;
  }

  const spanOnSpeech = span > 0 ? hit / span : 0;
  const recall = speech > 0 ? speechCovered / speech : 1;
  const plausible = segs.length > 0 ? 1 - implausible / segs.length : 1;
  return 0.35 * spanOnSpeech + 0.35 * recall + 0.3 * plausible;
}

/* ------------------------------------------------------------------ */
/* 6 — the stage                                                       */
/* ------------------------------------------------------------------ */

export interface OnsetAlignOptions {
  weights?: Partial<AlignWeights>;
  /**
   * How much local score a burst may lose and still keep the new boundaries.
   *
   * Not zero, and that is the single most important number in this file. The
   * local score measures span-on-speech, recall and duration plausibility — it
   * is blind to PLACEMENT, which is the error being corrected. Demanding no
   * regression at all therefore rejects almost every genuine improvement: on the
   * calibration file six bursts whose boundaries were pure proportional guesses
   * scored a flat 1.000 before and 0.975–0.985 after, because a correctly placed
   * boundary leaves a few frames of breath uncovered and a wrong one does not.
   * The tolerance is small enough that a burst which actually breaks — a segment
   * shoved into silence, or a label starved — still fails it.
   */
  guardTolerance?: number;
}

/** See {@link OnsetAlignOptions.guardTolerance}. */
const DEFAULT_GUARD_TOLERANCE = 0.04;

/** The whole stage is discarded if the file-wide score drops by more than this. */
const GLOBAL_GUARD_TOLERANCE = 0.01;

/** What happened to one burst, for the run log. */
export interface BurstAlignLog {
  burstIndex: number;
  startSec: number;
  endSec: number;
  /** Segments sitting in this burst. */
  segmentCount: number;
  /** Spectral onsets found inside it. Compare with {@link segmentCount}. */
  onsetCount: number;
  candidateCount: number;
  cost?: number;
  scoreBefore: number;
  scoreAfter: number;
  applied: boolean;
  note?: string;
}

export interface OnsetAlignResult<T extends LabelledSpan> {
  segments: T[];
  /** True when at least one burst was re-cut. */
  applied: boolean;
  bursts: BurstAlignLog[];
  /** Bursts whose new boundaries were accepted. */
  burstsApplied: number;
  /** Bursts examined with two or more segments. */
  burstsEligible: number;
  /** Segments whose start moved by more than 10 ms. */
  segmentsMoved: number;
  meanShiftSec: number;
  maxShiftSec: number;
}

/**
 * Re-cut every multi-segment burst onto acoustic onsets.
 *
 * `segments` must be time-ordered and non-overlapping (which is what
 * `refineBoundaries` guarantees by the time this runs). The result is the same
 * list with `startSec`/`endSec` replaced where a burst was improved; labels,
 * kinds and confidences are never touched.
 */
export function alignToOnsets<T extends LabelledSpan>(
  segments: readonly T[],
  ctx: {
    env: EnvelopeLike;
    bursts: readonly SpanSec[];
    onsets: readonly OnsetCandidate[];
    durationSec: number;
  },
  options: OnsetAlignOptions = {},
): OnsetAlignResult<T> {
  const weights = { ...DEFAULT_WEIGHTS, ...options.weights };
  const tolerance = options.guardTolerance ?? DEFAULT_GUARD_TOLERANCE;
  const { env, bursts, onsets } = ctx;

  const idle: OnsetAlignResult<T> = {
    segments: segments.map((s) => ({ ...s })),
    applied: false,
    bursts: [],
    burstsApplied: 0,
    burstsEligible: 0,
    segmentsMoved: 0,
    meanShiftSec: 0,
    maxShiftSec: 0,
  };
  if (segments.length === 0 || bursts.length === 0) return idle;

  /* -- candidates per burst, computed once ---------------------------- */
  const perBurst = bursts.map((b) => {
    const inside = onsets.filter((o) => o.timeSec >= b.startSec - 1e-9 && o.timeSec < b.endSec);
    const dips = detectDips(env, b.startSec, b.endSec);
    return [...inside, ...dips].sort((x, y) => x.timeSec - y.timeSec);
  });

  /* -- 1. which burst holds which run of segments --------------------- */
  const groups = partitionByOverlap(segments, bursts);

  /* -- 2. place each group, guarded burst by burst --------------------- */
  const out = segments.map((s) => ({ ...s }));
  const logs: BurstAlignLog[] = [];
  let burstsApplied = 0;
  let eligible = 0;
  let shiftSum = 0;
  let shiftMax = 0;
  let moved = 0;

  for (let b = 0; b < bursts.length; b++) {
    const burst = bursts[b];
    const [from, to] = groups[b];
    const K = to - from;
    const log: BurstAlignLog = {
      burstIndex: b,
      startSec: round3(burst.startSec),
      endSec: round3(burst.endSec),
      segmentCount: K,
      onsetCount: perBurst[b].filter((c) => c.source === 'flux').length,
      candidateCount: 0,
      scoreBefore: 0,
      scoreAfter: 0,
      applied: false,
    };

    if (K < 2) {
      // A burst with one segment carries no intra-burst placement decision, and
      // re-cutting it is actively harmful: a lone label over a 2.6 s burst is a
      // swallower whose neighbours the first pass never heard, and trimming it
      // to a plausible length would make the label look right while leaving two
      // seconds of speech unlabelled. Burst re-segmentation, which can ask what
      // is actually in there, is the stage for that.
      if (K === 1) log.note = 'single segment; nothing to place';
      logs.push(log);
      continue;
    }
    eligible++;

    const group = segments.slice(from, to);

    /*
     * Place inside the BURST, widened to whatever the group already covers.
     *
     * Confining the group strictly to its burst looks tidier and costs recall:
     * a group routinely spills into the silence on either side (the DTW pushed
     * "بتمنى" from 27.96 s across a 120 ms gap into the next burst), and cutting
     * that spill off leaves the speech under it unlabelled — measured at 0.27 s
     * of unlabelled speech before, 2.36 s after. The window therefore starts at
     * whichever is earlier, the burst or the group, and ends at whichever is
     * later, clamped so it can never reach into a neighbouring group.
     */
    const lo = Math.max(
      from > 0 ? segments[from - 1].endSec : 0,
      Math.min(burst.startSec, group[0].startSec),
    );
    const hi = Math.min(
      to < segments.length ? segments[to].startSec : ctx.durationSec,
      Math.max(burst.endSec, group[K - 1].endSec),
    );
    const placement =
      hi - lo > 0 ? placeSegmentsInBurst(group, lo, hi, perBurst[b], weights) : null;
    if (!placement) {
      log.note = 'no feasible placement';
      logs.push(log);
      continue;
    }
    log.candidateCount = placement.candidateCount;
    log.cost = round3(placement.cost);
    if (placement.crammed) log.note = 'burst too short for its segments';

    const rebuilt = group.map((s, k) => ({
      ...s,
      startSec: round3(placement.starts[k]),
      endSec: round3(placement.ends[k]),
    }));

    log.scoreBefore = round3(scoreBurst(group, env, burst.startSec, burst.endSec));
    log.scoreAfter = round3(scoreBurst(rebuilt, env, burst.startSec, burst.endSec));

    if (log.scoreAfter < log.scoreBefore - tolerance) {
      log.note = log.note ? `${log.note}; local score dropped, reverted` : 'local score dropped, reverted';
      logs.push(log);
      continue;
    }

    for (let k = 0; k < K; k++) {
      const shift = Math.abs(rebuilt[k].startSec - segments[from + k].startSec);
      shiftSum += shift;
      if (shift > shiftMax) shiftMax = shift;
      if (shift > 0.01) moved++;
      out[from + k] = rebuilt[k] as T;
    }
    log.applied = true;
    burstsApplied++;
    logs.push(log);
  }

  /* -- 3. final invariants -------------------------------------------- */
  repair(out, ctx.durationSec);

  /* -- 4. file-wide guard --------------------------------------------- */
  /*
   * The per-burst guard tolerates a small local loss, on the argument that the
   * score is blind to placement. Tolerating it 28 times over could still add up
   * to a worse file, so the same mix is applied once across the whole recording
   * with a much tighter bound. If it fails, the DTW boundaries stand.
   */
  if (burstsApplied > 0) {
    const whole = { startSec: 0, endSec: ctx.durationSec };
    const globalBefore = scoreBurst(segments, env, whole.startSec, whole.endSec);
    const globalAfter = scoreBurst(out, env, whole.startSec, whole.endSec);
    if (globalAfter < globalBefore - GLOBAL_GUARD_TOLERANCE) {
      return {
        ...idle,
        bursts: logs.map((l) => ({ ...l, applied: false, note: 'stage reverted: file-wide score dropped' })),
        burstsEligible: eligible,
      };
    }
  }

  return {
    segments: out,
    applied: burstsApplied > 0,
    bursts: logs,
    burstsApplied,
    burstsEligible: eligible,
    segmentsMoved: moved,
    meanShiftSec: round3(shiftSum / Math.max(1, segments.length)),
    maxShiftSec: round3(shiftMax),
  };
}

/* ------------------------------------------------------------------ */
/* Partitioning                                                        */
/* ------------------------------------------------------------------ */

type Group = [from: number, to: number];

/** The status quo: each segment joins the burst it overlaps most. */
function partitionByOverlap(segments: readonly LabelledSpan[], bursts: readonly SpanSec[]): Group[] {
  const groups: Group[] = bursts.map(() => [0, 0]);
  const home = segments.map((s) => {
    let best = -1;
    let bestOverlap = 0;
    for (let i = 0; i < bursts.length; i++) {
      const overlap = Math.min(s.endSec, bursts[i].endSec) - Math.max(s.startSec, bursts[i].startSec);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        best = i;
      }
    }
    if (best >= 0) return best;
    // Sitting in silence: nearest burst wins, so it still belongs somewhere.
    let nearest = 0;
    let dist = Infinity;
    for (let i = 0; i < bursts.length; i++) {
      const d =
        s.startSec >= bursts[i].endSec ? s.startSec - bursts[i].endSec : bursts[i].startSec - s.endSec;
      if (d < dist) {
        dist = d;
        nearest = i;
      }
    }
    return nearest;
  });

  // The mapping is forced monotone: a segment can never be assigned to a burst
  // before its predecessor's, or the groups would not be contiguous runs.
  let cursor = 0;
  const fixed = home.map((h) => {
    const v = Math.max(h, cursor);
    cursor = v;
    return v;
  });
  let i = 0;
  for (let b = 0; b < bursts.length; b++) {
    const from = i;
    while (i < fixed.length && fixed[i] === b) i++;
    groups[b] = [from, i];
  }
  if (i < segments.length && bursts.length > 0) groups[bursts.length - 1][1] = segments.length;
  return groups;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Order, non-overlap, minimum length, inside the file. */
function repair(segments: LabelledSpan[], durationSec: number): void {
  let cursor = 0;
  for (const s of segments) {
    let start = Math.max(s.startSec, cursor);
    let end = Math.max(s.endSec, start + 0.06);
    if (end > durationSec) {
      end = durationSec;
      start = Math.min(start, Math.max(0, end - 0.06));
    }
    s.startSec = round3(start);
    s.endSec = round3(end);
    cursor = s.endSec;
  }
}

export default alignToOnsets;
